import { Platform } from 'models/platforms/platform';
import { Manufacturer } from 'models/platforms/manufacturer';
import {
  AdditionalTizenKeyMapping,
  TizenKeyMapping,
  TizenOneTrustKeyMapping,
} from 'models/platforms/tizenKeyMappings';
import { OneTrustRemoteKeys } from 'types/oneTrust';
import {
  TizenWindow,
  TvInfoMenuKey,
  TvInfoMenuValue,
  TVNetworkStateValue,
} from 'models/platforms/tizenWindow';
import { AppData, Registry } from '@lightningjs/sdk';
import Router from '@lightningjs/sdk/src/Router';
import {
  CcEdgeType,
  translateCcSettingToCss,
} from 'config/platforms/deviceIntegration';
import { ApplicationInstance } from '@lightningjs/sdk/src/Launch';
import { getPageIdFromHash } from 'support/routerUtils';
import { PageId } from 'types/pageId';
import { constants } from 'aliases';
import { RoutePath } from 'config/routes';
import { TizenServiceIntegration } from 'config/platforms/tizen/TizenServiceIntegration';
import {
  ErrorCode,
  reportError,
} from 'services/analytics/reportingServicePage';
import { createDeepLinkUrl, DeepLinkUrlArgs } from 'support/deepLinkUtils';
import { AbstractMouseDeviceIntegration } from '../AbstractMouseDeviceIntegration';

type TvInfoMenuValueStringMap = Partial<Record<TvInfoMenuValue, string>>;
type TvInfoMenuValueEdgeTypeMap = Partial<Record<TvInfoMenuValue, CcEdgeType>>;
type TvInfoMenuValueNumberMap = Partial<Record<TvInfoMenuValue, number>>;

/**
 * @member CHANNEL Navigates to EPG page
 * @member CONTENT_HUB Navigates to Content Hub page
 * @member EPISODE Navigates to VOD Playback page
 * @member LIVE_EVENT Navigates to Live Event Details or Live page
 * @member MOVIE Navigates to Movie Details page
 * @member SERIES Navigates to Series Details page
 **/
enum PAYLOAD_CONTENT_TYPE {
  CHANNEL = 'channel',
  CONTENT_HUB = 'content_hub',
  EPISODE = 'episode',
  LIVE_EVENT = 'live_event',
  MOVIE = 'movie',
  SERIES = 'series',
}

type ChannelDeepLinkPayload = {
  content_type: PAYLOAD_CONTENT_TYPE.CHANNEL;
  id?: string;
};
type ContentHubDeepLinkPayload = {
  content_type: PAYLOAD_CONTENT_TYPE.CONTENT_HUB;
  id: string;
};
type EpisodeDeepLinkPayload = {
  content_type: PAYLOAD_CONTENT_TYPE.EPISODE;
  id: string;
};
type LiveEventDeepLinkPayload = {
  content_type: PAYLOAD_CONTENT_TYPE.LIVE_EVENT;
  id: string;
};
type MovieDeepLinkPayload = {
  content_type: PAYLOAD_CONTENT_TYPE.MOVIE;
  id: string;
};
type SeriesDeepLinkPayload = {
  content_type: PAYLOAD_CONTENT_TYPE.SERIES;
  id: string;
  season?: number;
};

type DeepLinkPayload =
  | ChannelDeepLinkPayload
  | ContentHubDeepLinkPayload
  | EpisodeDeepLinkPayload
  | LiveEventDeepLinkPayload
  | MovieDeepLinkPayload
  | SeriesDeepLinkPayload;

const FLASH_INTERVAL = constants.timers.subtitleFlashingInterval;
const FLICKERING_ISSUE_MODELS = constants.flickeringIssueModels.tizen;

export class TizenDeviceIntegration extends AbstractMouseDeviceIntegration {
  readonly platform = Platform.TIZEN;

  readonly tizen = window.tizen;
  readonly colorMap: TvInfoMenuValueStringMap = {
    [TvInfoMenuValue.CAPTION_COLOR_BLACK]: '#000000',
    [TvInfoMenuValue.CAPTION_COLOR_BLUE]: '#0000ff',
    [TvInfoMenuValue.CAPTION_COLOR_CYAN]: '#00ffff',
    [TvInfoMenuValue.CAPTION_COLOR_DEFAULT]: '#000000',
    [TvInfoMenuValue.CAPTION_COLOR_GREEN]: '#00ff00',
    [TvInfoMenuValue.CAPTION_COLOR_MAGENTA]: '#ff00ff',
    [TvInfoMenuValue.CAPTION_COLOR_RED]: '#ff0000',
    [TvInfoMenuValue.CAPTION_COLOR_WHITE]: '#ffffff',
    [TvInfoMenuValue.CAPTION_COLOR_YELLOW]: '#ffff00',
  };
  readonly opacityMap: TvInfoMenuValueStringMap = {
    [TvInfoMenuValue.CAPTION_OPACITY_DEFAULT]: 'ff',
    [TvInfoMenuValue.CAPTION_OPACITY_FLASH]: 'ff',
    [TvInfoMenuValue.CAPTION_OPACITY_HIGHLY_TRANSLUCENT]: '54',
    [TvInfoMenuValue.CAPTION_OPACITY_SLIGHTLY_TRANSLUCENT]: 'ac',
    [TvInfoMenuValue.CAPTION_OPACITY_SOLID]: 'ff',
    [TvInfoMenuValue.CAPTION_OPACITY_TRANSLUCENT]: '80',
    [TvInfoMenuValue.CAPTION_OPACITY_TRANSPARENT]: '00',
  };
  readonly fontSizeMap: TvInfoMenuValueNumberMap = {
    [TvInfoMenuValue.CAPTION_SIZE_DEFAULT]: 48,
    [TvInfoMenuValue.CAPTION_SIZE_EXTRA_LARGE]: 72,
    [TvInfoMenuValue.CAPTION_SIZE_LARGE]: 60,
    [TvInfoMenuValue.CAPTION_SIZE_SMALL]: 24,
    [TvInfoMenuValue.CAPTION_SIZE_STANDARD]: 36,
  };
  readonly fontFaceMap: TvInfoMenuValueStringMap = {
    [TvInfoMenuValue.CAPTION_FONT_DEFAULT]: 'Roboto',
    [TvInfoMenuValue.CAPTION_FONT_STYLE0]: 'Roboto',
    [TvInfoMenuValue.CAPTION_FONT_STYLE1]: 'Courier',
    [TvInfoMenuValue.CAPTION_FONT_STYLE2]: 'Times New Roman',
    [TvInfoMenuValue.CAPTION_FONT_STYLE3]: 'Helvetica',
    [TvInfoMenuValue.CAPTION_FONT_STYLE4]: 'Arial',
    [TvInfoMenuValue.CAPTION_FONT_STYLE5]: 'Dom',
    [TvInfoMenuValue.CAPTION_FONT_STYLE6]: 'Coronet',
    // @ts-ignore Additional font style not found in docs
    [TvInfoMenuValue.CAPTION_FONT_STYLE7]: 'Engravers Gothic',
  };
  readonly edgeTypeMap: TvInfoMenuValueEdgeTypeMap = {
    [TvInfoMenuValue.CAPTION_EDGE_DEPRESSED]: '2px 0px 2px',
    [TvInfoMenuValue.CAPTION_EDGE_DROP_SHADOWED]: '-4px -4px 4px',
    [TvInfoMenuValue.CAPTION_EDGE_NONE]: '0px 0px 0px',
    [TvInfoMenuValue.CAPTION_EDGE_RAISED]: '-2px -2px 2px',
    [TvInfoMenuValue.CAPTION_EDGE_UNIFORM]: '0px 0px 4px',
  };
  readonly fontStyleMap: TvInfoMenuValueStringMap = {
    [TvInfoMenuValue.CAPTION_STYLE_BOLD]: 'bold',
    [TvInfoMenuValue.CAPTION_STYLE_DEFAULT]: 'normal',
    [TvInfoMenuValue.CAPTION_STYLE_ITALIC]: 'italic',
  };
  readonly serviceIntegration: TizenServiceIntegration;

  private _announcerChangeCallback: ((arg: boolean) => void) | null = null;
  private _networkChangeCallback: ((arg: boolean) => void) | null = null;
  private _flashTimer: number | null = null;
  private _flashOn = true;

  protected override isSwipeSupported = true;

  constructor() {
    super();
    this.deviceInfo.manufacturer = Manufacturer.SAMSUNG;

    this.serviceIntegration = new TizenServiceIntegration(this);
  }

  override getKeyMapping() {
    return TizenKeyMapping;
  }

  // Tizen needs a larger delay for unclear reasons.  Suspected slower/different navigation handling from SDK
  override getInteractiveAdExitDelay(): number {
    return 1000;
  }

  override getOneTrustKeyMapping(): OneTrustRemoteKeys {
    return {
      ...super.getOneTrustKeyMapping(),
      ...TizenOneTrustKeyMapping,
    };
  }

  override getDeviceId(): string {
    if (!this.deviceId) {
      this.deviceId = this.tizen.systeminfo.getCapability(
        'http://tizen.org/system/tizenid',
      );
    }

    return this.deviceId;
  }

  override hasFlickerIssue(): boolean {
    return FLICKERING_ISSUE_MODELS.includes(this.deviceInfo.model);
  }

  override isLowPerformanceModel(): boolean {
    // Tizen 4.0 devices are low performance, especially during playback.
    return this.deviceInfo.osVersion === '4.0';
  }

  override isLowMemoryModel(): boolean {
    return false;
  }

  protected override isOneTrustSupportedOs(): boolean {
    return (
      parseFloat(this.deviceInfo.osVersion) >=
      constants.oneTrust.minTizenVersion
    );
  }

  override getAppId(): string {
    const { id } = this.tizen.application.getAppInfo();
    return id;
  }

  getPackageId(): string {
    const { packageId } = this.tizen.application.getAppInfo();
    return packageId;
  }

  getAppName() {
    const { name } = this.tizen.application.getAppInfo();
    return name;
  }

  getAppIconPath() {
    const { iconPath } = this.tizen.application.getAppInfo();
    return iconPath;
  }

  getSubmittedAppVersion() {
    return tizen.application.getCurrentApplication().appInfo.version;
  }

  onAdPodStart(): void {
    // Not Supported
  }

  onAdPodEnd(): void {
    // Not Supported
  }

  onPlayerExit(): void {
    // Not Supported
  }

  override async load() {
    return new Promise<void>((resolve, reject) => {
      // To load Tizen web APIs, we need a script call in the heading of our index.html file. Since LightningJS only
      // utilizes one document to create the app, the document will always be the index.html file. Once the script is
      // loaded, we can resolve the window.webapis call. Read more about utilizing web APIs in the following link:
      // https://developer.samsung.com/smarttv/develop/api-references/samsung-product-api-references.html
      const webapisElem = document.createElement('script');
      webapisElem.src = '$WEBAPIS/webapis/webapis.js';
      document.getElementsByTagName('head')[0]?.appendChild(webapisElem);

      webapisElem.onerror = reject;
      webapisElem.onload = () => {
        const webapis = (window as unknown as TizenWindow).webapis;
        if (webapis && webapis.productinfo) {
          const { productinfo, adinfo, network } = webapis;

          this.deviceInfo.osVersion = this.tizen.systeminfo.getCapability(
            'http://tizen.org/feature/platform.version',
          );

          this.deviceInfo.model = productinfo.getRealModel();
          this.deviceInfo.adId = adinfo.getTIFA();
          this.deviceInfo.isLat = adinfo.isLATEnabled();
          this.deviceInfo.ifaType = 'tifa';
          this.deviceInfo.ip = network.getIp();

          network.addNetworkStateChangeListener(networkState =>
            this.parseNetworkChange(networkState),
          );
        }

        // Listen for deep links
        Registry.addEventListener(window, 'appcontrol', () => {
          // If user navigates via deep link on the splash page, we do not need to handle it here since
          // user will be redirected once splash page has finished loading
          if (getPageIdFromHash(Router.getActiveHash() ?? '') === PageId.SPLASH)
            return;

          AppData!.isDeepLinkEntry = true;
          this.handleDeepLink();
        });

        if (webapis) {
          const { tvinfo } = webapis;
          Object.values(TvInfoMenuKey).forEach(menuKey => {
            try {
              // Initialize CC Settings
              this.updateCcSetting(menuKey, tvinfo.getMenuValue(menuKey));

              // Listen for CC settings changes
              tvinfo.addCaptionChangeListener(menuKey, menuKey =>
                this.updateCcSetting(menuKey, tvinfo.getMenuValue(menuKey)),
              );
            } catch (e) {
              // Some Tizen devices may not support all available menu keys, and will be caught here
              reportError({ error: e, userFacing: false, code: ErrorCode.c11 });
            }
          });
        }

        // Register additional keys that will take effect when pressed on the remote
        // Note: this also overrides their default functionality
        this.tizen.tvinputdevice.registerKeyBatch(
          Object.values(AdditionalTizenKeyMapping),
        );

        this.setupMouseHandling();

        resolve();
      };
    });
  }

  override onAppActive() {
    // Do nothing
  }

  override beforeAppClose(callback: (showPopup: boolean) => void) {
    /**
     * For Tizen's Deep link Return Key Policy:
     * After the user enters the application through a deep link, the “Return/Exit” key click must
     * be implemented to perform the following actions:
     * ...
     * - From the home page of an application, clicking the “Return/Exit” key must
     * terminate the application without any confirmation popup. The user is returned to Smart Hub Preview.
     * */
    if (AppData?.isDeepLinkEntry) {
      // Based on Tizen's deep link return key policy, we will close the app when user presses back on the nav bar
      callback(false);
    }
    callback(true);
  }

  override closeApp() {
    this.tizen.application.getCurrentApplication().exit();
  }

  private isStringifiedJson(data: unknown) {
    if (typeof data !== 'string') return false;

    try {
      const parsedData = JSON.parse(data);
      return !!parsedData && typeof parsedData === 'object';
    } catch (e) {
      reportError({ error: e, userFacing: false, code: ErrorCode.c12 });
      return false;
    }
  }

  /**
   * Performs a depth first search on the deep link payload to build a deep link payload
   *
   * @param actionData The data we are searching to build a deep link payload
   * @returns A deep link payload. Return `undefined` if no payload can be built
   */
  private parseDeepLinkPayload(
    actionData: unknown,
  ): DeepLinkPayload | undefined {
    const availableContentTypes: string[] = Object.values(PAYLOAD_CONTENT_TYPE);
    let contentType: PAYLOAD_CONTENT_TYPE | undefined;
    let id: string | undefined;
    let season: number | undefined;

    const uncheckedNodes = [actionData];
    while (uncheckedNodes.length) {
      let currentNode = uncheckedNodes.pop();

      // Attempt to parse current node to JSON object
      if (this.isStringifiedJson(currentNode)) {
        currentNode = JSON.parse(currentNode as string);
      }

      // We can't use the current node if it's undefined or not an object
      if (!currentNode || typeof currentNode !== 'object') continue;

      if (
        'content_type' in currentNode &&
        typeof currentNode['content_type'] === 'string' &&
        availableContentTypes.includes(currentNode['content_type'])
      ) {
        contentType = currentNode['content_type'] as PAYLOAD_CONTENT_TYPE;
      }

      if ('id' in currentNode && typeof currentNode['id'] === 'string') {
        id = currentNode['id'] as string;
      }

      if (
        'season' in currentNode &&
        typeof currentNode['season'] === 'number'
      ) {
        season = currentNode['season'] as number;
      }

      if (contentType) {
        const deepLinkPayload = this.buildDeepLinkPayload({
          contentType,
          id,
          season,
        });
        if (deepLinkPayload) return deepLinkPayload;
      }

      Object.values(currentNode).forEach(value => {
        uncheckedNodes.push(value);
      });
    }

    return undefined;
  }

  private buildDeepLinkPayload(data: {
    contentType: PAYLOAD_CONTENT_TYPE;
    id?: string;
    season?: number;
  }): DeepLinkPayload | undefined {
    const { contentType, id, season } = data;
    switch (contentType) {
      case 'channel':
        // ID is optional
        return {
          content_type: contentType,
          id,
        };
      case 'series':
        // ID is not optional, but season is
        if (!id) return undefined;
        return {
          content_type: contentType,
          id,
          season,
        };
      case 'content_hub':
      case 'episode':
      case 'movie':
      case 'live_event':
        // ID is not optional
        if (!id) return undefined;
        return {
          content_type: contentType,
          id,
        };
    }
  }

  override handleDeepLink() {
    try {
      const requestedAppControl = this.tizen.application
        .getCurrentApplication()
        .getRequestedAppControl();

      const appControlData = requestedAppControl.appControl.data;
      const payload = appControlData.find(item => {
        return item.key == 'PAYLOAD';
      })!;

      if (!payload) return false;

      const payloadData = payload.value[0]!;
      const actionData = JSON.parse(payloadData).values as string;

      // The logic above is interpreted directly from Tizen's deep link documentation.
      // However, we have not confirmed how Tizen will interpret the deep link payload.
      // Therefore, we created a deep link payload parser that performs a wide check on
      // the payload to search for the pertinent deep link values `content_type` and `id`
      const parsedDeepLinkPayload = this.parseDeepLinkPayload(actionData);
      if (!parsedDeepLinkPayload) return false;

      // Convert deep link payload to URL path format
      const urlPath = this.convertDeepLinkPayloadToPath(parsedDeepLinkPayload);
      if (!urlPath) return false;

      // Use handleDeepLinkURL to handle navigation
      this.handleDeepLinkURL(urlPath.path, urlPath.params);

      AppData?.modalService.close();
      return true;
    } catch (e) {
      reportError({ error: e, userFacing: true, code: ErrorCode.c13 });
      return false;
    }
  }

  private convertDeepLinkPayloadToPath(
    payload: DeepLinkPayload,
  ): { path: string; params?: Router.NavigateArgs } | undefined {
    const { content_type, id } = payload;

    let route: RoutePath | null = null;
    const args: DeepLinkUrlArgs = {};

    switch (content_type) {
      case PAYLOAD_CONTENT_TYPE.CHANNEL: {
        route = RoutePath.EPG;
        break;
      }
      case PAYLOAD_CONTENT_TYPE.SERIES: {
        route = RoutePath.SERIES_DETAIL;
        args.season = (payload as SeriesDeepLinkPayload).season;
        break;
      }
      case PAYLOAD_CONTENT_TYPE.MOVIE:
        route = RoutePath.MOVIE_DETAIL;
        break;
      case PAYLOAD_CONTENT_TYPE.EPISODE:
        route = RoutePath.PLAY;
        break;
      case PAYLOAD_CONTENT_TYPE.LIVE_EVENT:
        route = RoutePath.LIVE_EVENT_DETAIL;
        break;
      case PAYLOAD_CONTENT_TYPE.CONTENT_HUB:
        route = RoutePath.CONTENT_HUB;
        break;
      default:
        break;
    }

    if (!route || !id) return undefined;

    return { path: createDeepLinkUrl(route, id, args) };
  }

  override handleNetworkChange(callback: (arg: boolean) => void) {
    this._networkChangeCallback = callback;
  }

  override setupMouseDetectionHandling() {
    Registry.addEventListener(document, 'mouseenter', (event: MouseEvent) => {
      if (!event.relatedTarget) {
        this.cursorVisibilityChange(true);
      }
    });

    Registry.addEventListener(document, 'mouseleave', (event: MouseEvent) => {
      if (!event.relatedTarget) {
        this.cursorVisibilityChange(false);
      }
    });
  }

  /**
   * @param state
   * true - enable screensaver
   * false - disable screensaver
   */
  override setScreenSaver(state: boolean) {
    const webapis = (window as unknown as TizenWindow).webapis;
    const appCommon = webapis && webapis.appcommon;
    if (!appCommon) return;

    const onOffScreenSaver = state
      ? appCommon.AppCommonScreenSaverState.SCREEN_SAVER_ON
      : appCommon.AppCommonScreenSaverState.SCREEN_SAVER_OFF;
    appCommon.setScreenSaver(onOffScreenSaver);
  }

  override getAnnouncerEnabled() {
    /**
     * Assume announcer is off until webapis are loaded. Then, we can confirm
     * the announcer's state with the announcer change callback
     */
    return false;
  }

  override shouldAnnounceContext(): boolean {
    return true;
  }

  override shouldAnnounceSeeking(): boolean {
    return true;
  }

  override shareHistoryPlatformName(): string {
    return 'Samsung';
  }

  handleAnnouncerChange(callback: (arg: boolean) => void): void {
    this._announcerChangeCallback = callback;
  }

  handleCcChange(callback: (enabled: boolean) => void): void {
    // Not Supported
  }

  useTextTrackComponent(): boolean {
    return false;
  }

  private updateCcSetting(
    key: (typeof TvInfoMenuKey)[keyof typeof TvInfoMenuKey],
    value: TvInfoMenuValue,
  ) {
    const style =
      document.getElementById('caption-styles') ??
      document.createElement('style');
    style.id = 'caption-styles';

    switch (key) {
      case TvInfoMenuKey.CAPTION_FG_COLOR_KEY:
        // Default font color to white
        if (value === TvInfoMenuValue.CAPTION_COLOR_DEFAULT) {
          value = TvInfoMenuValue.CAPTION_COLOR_WHITE;
        }

        this._ccSettings.color = this.colorMap[value];
        break;
      case TvInfoMenuKey.CAPTION_BG_COLOR_KEY:
        this._ccSettings.backgroundColor = this.colorMap[value];
        break;
      case TvInfoMenuKey.CAPTION_FG_OPACITY_KEY:
        this._ccSettings.colorFlashing =
          value === TvInfoMenuValue.CAPTION_OPACITY_FLASH;

        this._ccSettings.colorOpacity = this.opacityMap[value];
        break;
      case TvInfoMenuKey.CAPTION_BG_OPACITY_KEY:
        this._ccSettings.backgroundFlashing =
          value === TvInfoMenuValue.CAPTION_OPACITY_FLASH;

        this._ccSettings.backgroundOpacity = this.opacityMap[value];
        break;
      case TvInfoMenuKey.CAPTION_FONT_SIZE_KEY:
        this._ccSettings.fontSize = this.fontSizeMap[value];
        break;
      case TvInfoMenuKey.CAPTION_ONOFF_KEY:
        this._ccSettings.enabled = value === TvInfoMenuValue.CAPTION_ON;
        ApplicationInstance?.emit(
          'ccChange',
          value === TvInfoMenuValue.CAPTION_ON,
        );
        break;
      case TvInfoMenuKey.CAPTION_FONT_STYLE_KEY:
        this._ccSettings.fontFace = this.fontFaceMap[value];
        break;
      case TvInfoMenuKey.CAPTION_EDGE_TYPE_KEY:
        this._ccSettings.edgeType = this.edgeTypeMap[value];
        break;
      case TvInfoMenuKey.CAPTION_EDGE_COLOR_KEY:
        this._ccSettings.edgeColor = this.colorMap[value];
        break;
      case TvInfoMenuKey.CAPTION_WINDOW_COLOR_KEY:
        // Not used by default
        if (value === TvInfoMenuValue.CAPTION_COLOR_DEFAULT) {
          this._ccSettings.windowColor = undefined;
          break;
        }

        this._ccSettings.windowColor = this.colorMap[value];
        break;
      case TvInfoMenuKey.CAPTION_WINDOW_OPACITY_KEY:
        this._ccSettings.windowFlashing =
          value === TvInfoMenuValue.CAPTION_OPACITY_FLASH;

        this._ccSettings.windowOpacity = this.opacityMap[value];
        break;
      case TvInfoMenuKey.CAPTION_STYLE_KEY:
        this._ccSettings.fontStyle = this.fontStyleMap[value];
        break;
      case TvInfoMenuKey.VOICE_GUIDE_KEY:
        this._announcerChangeCallback?.(value === 1);
        break;
      default:
        break;
    }

    if (
      !this._flashTimer &&
      (this._ccSettings.backgroundFlashing || this._ccSettings.colorFlashing)
    ) {
      this._flashTimer = Registry.setInterval(() => {
        if (this._ccSettings.colorFlashing) {
          this._ccSettings.colorOpacity = this._flashOn ? 'ff' : '00';
        }
        if (this._ccSettings.backgroundFlashing) {
          this._ccSettings.backgroundOpacity = this._flashOn ? 'ff' : '00';
        }
        if (this._ccSettings.windowFlashing) {
          this._ccSettings.windowOpacity = this._flashOn ? 'ff' : '00';
        }

        const style =
          document.getElementById('caption-styles') ??
          document.createElement('style');
        style.id = 'caption-styles';
        style.innerHTML = translateCcSettingToCss(this._ccSettings);
        document.head.appendChild(style);

        this._flashOn = !this._flashOn;
      }, FLASH_INTERVAL);
    } else {
      if (this._flashTimer) {
        Registry.clearInterval(this._flashTimer);
        this._flashTimer = null;
      }
      style.innerHTML = translateCcSettingToCss(this._ccSettings);
      document.head.appendChild(style);
    }
  }

  private parseNetworkChange(networkState: number) {
    switch (networkState) {
      case TVNetworkStateValue.GATEWAY_CONNECTED:
      case TVNetworkStateValue.INTERNET_CONNECTED:
      case TVNetworkStateValue.LAN_CABLE_ATTACHED:
      case TVNetworkStateValue.WIFI_MODULE_STATE_ATTACHED:
        this._networkChangeCallback?.(true);
        break;
      case TVNetworkStateValue.GATEWAY_DISCONNECTED:
      case TVNetworkStateValue.INTERNET_DISCONNECTED:
      case TVNetworkStateValue.LAN_CABLE_DETACHED:
      case TVNetworkStateValue.LAN_CABLE_STATE_UNKNOWN:
      case TVNetworkStateValue.WIFI_MODULE_STATE_DETACHED:
      case TVNetworkStateValue.WIFI_MODULE_STATE_UNKNOWN:
        this._networkChangeCallback?.(false);
        break;
    }
  }
}
